import { z } from 'zod';

/**
 * Stan materiału. Rdzeń decyzji D1: nie odtwarzamy niczego zdalnie — najpierw
 * pobieramy plik u siebie, pokazując postęp w playerze, i dopiero potem serwujemy.
 * Dzięki temu synchronizacja, napisy ASS, seek i progres działają identycznie
 * dla zescrapowanych źródeł i dla własnych uploadów.
 */
export const MEDIA_STATES = [
  'queued',
  'acquiring',
  'probing',
  'processing',
  'ready',
  'failed',
] as const;

export type MediaState = (typeof MEDIA_STATES)[number];

/** Stan, w którym materiału nie można jeszcze odtworzyć. */
export function isMediaPending(state: MediaState): boolean {
  return state !== 'ready' && state !== 'failed';
}

export const PROCESSING_STAGES = [
  /**
   * Pytanie źródeł o kopie odcinka — przed pobieraniem. Osobny etap od 12.10.2026:
   * Shinden wymaga odstępu kilkunastu sekund między wejściami, więc ten krok trwa
   * do pół minuty i bez nazwy wyglądał jak kliknięcie, które nic nie zrobiło.
   */
  'resolve',
  'download',
  'probe',
  'remux',
  'transcode',
  'thumbs',
  'subs',
] as const;

export type ProcessingStage = (typeof PROCESSING_STAGES)[number];

/** Polskie etykiety etapów — jedno źródło prawdy dla playera i admina. */
export const PROCESSING_STAGE_LABELS_PL: Record<ProcessingStage, string> = {
  resolve: 'Szukanie kopii',
  download: 'Pobieranie',
  probe: 'Analiza',
  remux: 'Przygotowanie',
  transcode: 'Przekodowanie',
  thumbs: 'Miniatury',
  subs: 'Napisy',
};

/**
 * Polskie etykiety stanów materiału — krótkie, do plakietek i list.
 *
 * Powstały, bo lista plików pokazywała surowe `acquiring` i `probing` wprost
 * z bazy: widz czytał w polskim interfejsie angielską nazwę stanu wewnętrznego,
 * której nie ma jak zrozumieć.
 *
 * Pełnoekranowy stan w odtwarzaczu ma **własne**, dłuższe nagłówki („Pobieram
 * materiał"), bo tam jest miejsce na zdanie i widz patrzy na to minutami. Te tutaj
 * muszą się zmieścić w plakietce obok rozmiaru pliku.
 */
export const MEDIA_STATE_LABELS_PL: Record<MediaState, string> = {
  queued: 'W kolejce',
  acquiring: 'Pobieram',
  probing: 'Sprawdzam',
  processing: 'Przygotowuję',
  ready: 'Gotowe',
  failed: 'Nieudane',
};

export interface MediaProgress {
  mediaFileId: string;
  state: MediaState;
  stage?: ProcessingStage;
  /** 0..100 dla etapów, które znają swój postęp (ffmpeg, pobieranie ze znanym rozmiarem). */
  stagePercent?: number;
  bytesDone?: number;
  /** null gdy host nie podaje Content-Length — UI pokazuje wtedy same MB i prędkość. */
  bytesTotal?: number | null;
  speedBps?: number;
  etaSec?: number;
  /** Która próba w kaskadzie playerów — UI pokazuje "pobieram z mp4upload (2/4)". */
  attempt?: { host: string; index: number; total: number };
  updatedAt: string;
}

/**
 * Ostrzeżenie o pobranym materiale — powód, żeby widz spojrzał, zanim zacznie.
 *
 * To NIE są porażki: plik jest i da się go odtworzyć. Chodzi o rzeczy, których
 * nie potrafimy rozstrzygnąć automatycznie, a które widz rozpozna w kilka sekund
 * (przegląd z 12.10.2026: kopia z tłumaczeniem maszynowym, odcinek innej serii
 * pod linkiem tego odcinka). Odpowiedzią jest przycisk „Zła wersja" obok.
 */
export type MediaWarning =
  | { kind: 'machine-translation' }
  | { kind: 'duration-mismatch'; actualSec: number; expectedSec: number }
  /** Napisy w języku innym niż polski, gdy źródło podaje język wprost. */
  | { kind: 'foreign-subs'; lang: string };

/** Powody odrzucenia kopii — z etykietami do przycisku „Zła wersja". */
export const REJECTION_REASONS = ['language', 'wrong-material', 'dub', 'broken'] as const;
export type RejectionReasonCode = (typeof REJECTION_REASONS)[number];

export const REJECTION_REASON_LABELS_PL: Record<RejectionReasonCode, string> = {
  language: 'Inny język napisów',
  'wrong-material': 'Inny odcinek albo inna seria',
  dub: 'Lektor zamiast napisów',
  broken: 'Uszkodzony obraz albo dźwięk',
};

/** Kody porażki zdobywania materiału. Widoczne w UI jako tabela prób. */
export const ACQUIRE_ERROR_CODES = [
  'BLOCKED',
  'EGRESS_UNAVAILABLE',
  'NOT_FOUND',
  'QUOTA',
  'TOO_LARGE',
  'NO_SPACE',
  'EXTRACT_FAILED',
  'TIMEOUT',
  'UPSTREAM_5XX',
  'CHECKSUM_MISMATCH',
  'UNSUPPORTED_HOST',
  'CANCELLED',
  /**
   * Proces został zatrzymany w trakcie pracy — na przykład przez wdrożenie.
   * Odrębny kod, bo to nie awaria hosta ani materiału: ponowienie ma pełne
   * szanse, a mieszanie tego z porażkami zafałszowałoby statystyki hostów.
   */
  'INTERRUPTED',
  /**
   * Host blokuje numer AS serwera, więc adresu nie da się uzyskać bez pomocy
   * z innej sieci. Odrębny kod, bo to nie porażka hosta ani materiału i nie
   * wolno go liczyć do statystyk niesprawności: przekaźnik po prostu nie był
   * dostępny. Interfejs pokazuje wtedy, co zrobić, a nie samą odmowę.
   */
  'RELAY_REQUIRED',
  /**
   * Przekaźnik BYŁ obecny, ale nie dostarczył manifestu na czas.
   *
   * Odrębny od `RELAY_REQUIRED`, bo prowadzi do zupełnie innej czynności.
   * Tamten znaczy „otwórz kartę ze skryptem" — tej karty nie ma. Ten znaczy
   * „karta jest, sprawdź, czym wychodzi do sieci": zmierzone na produkcji
   * 30.08.2026, gdy skrypt odpytywał normalnie, a osiem żądań pod rząd minęło
   * limit czasu, bo przeglądarka szła przez VPN i cda odmawiała TEMU adresowi.
   * Po wyłączeniu VPN manifest wracał w 1–5 sekund.
   *
   * Jedno i drugie jest odzyskiwalne bez naszej zmiany w kodzie, więc oba
   * podejmuje ta sama praca po powrocie przekaźnika.
   */
  'RELAY_TIMEOUT',
] as const;

export type AcquireErrorCode = (typeof ACQUIRE_ERROR_CODES)[number];

export const ACQUIRE_ERROR_LABELS_PL: Record<AcquireErrorCode, string> = {
  BLOCKED: 'Host odrzucił połączenie z serwera',
  EGRESS_UNAVAILABLE: 'Host wymaga tunelu, który nie jest skonfigurowany',
  NOT_FOUND: 'Materiał nie istnieje pod tym adresem',
  QUOTA: 'Limit pobrań hosta wyczerpany',
  TOO_LARGE: 'Plik przekracza dopuszczalny rozmiar',
  NO_SPACE: 'Brak miejsca na dysku',
  EXTRACT_FAILED: 'Nie udało się wyciągnąć adresu pliku',
  TIMEOUT: 'Przekroczono czas oczekiwania',
  UPSTREAM_5XX: 'Błąd po stronie hosta',
  CHECKSUM_MISMATCH: 'Pobrany plik jest niekompletny',
  UNSUPPORTED_HOST: 'Ten host nie jest obsługiwany',
  CANCELLED: 'Anulowano',
  INTERRUPTED: 'Przerwane restartem serwera — spróbuj ponownie',
  RELAY_REQUIRED: 'Ten host wymaga skryptu przeglądarkowego albo wklejenia adresu ręcznie',
  RELAY_TIMEOUT: 'Skrypt przeglądarkowy jest podłączony, ale nie odesłał adresu na czas',
};

/** Jedna próba zdobycia materiału — zapisywana w acquisitions.attempts_json. */
export interface AcquireAttempt {
  index: number;
  host: string;
  method: AcquireMethod;
  embedUrl: string;
  /**
   * Etykieta odtwarzacza ze źródła — u docchi z nazwą grupy („default — Shinden").
   *
   * Dopisana po przeglądzie z 12.10.2026: zgłoszenie „pobrała się zła wersja
   * językowa" wymagało odtworzenia ze stron źródła, KTÓRĄ grupę wzięliśmy, bo
   * tabela prób znała tylko host. Przy kilku kopiach na jednym hoście host nie
   * mówi nic, a strona źródła po tygodniu wygląda już inaczej.
   */
  label?: string;
  viaEgress: boolean;
  ok: boolean;
  errorCode?: AcquireErrorCode;
  httpStatus?: number;
  detail?: string;
  bytesTotal?: number | null;
  /**
   * Czas CAŁEJ próby, od jej rozpoczęcia do końca.
   *
   * Pole nazywało się wcześniej `ttfbMs`, czyli „czas do pierwszego bajtu",
   * i mierzyło coś innego: różnicę do KOŃCA pobrania. Zauważyłem to na realnych
   * danych — 730 sekund „do pierwszego bajtu" przy pobraniu, które trwało 730
   * sekund. Nazwa, która kłamie, jest gorsza od braku pola: w tym projekcie
   * pomiary napędzają decyzje o kolejności hostów.
   *
   * Prawdziwego czasu do pierwszego bajtu dziś NIE mierzymy — wymagałby zdarzenia
   * z pobieracza w chwili pierwszego odebranego bajta.
   */
  durationMs?: number;
  /**
   * Zmierzona przepustowość udanego pobrania.
   *
   * Zapisywana, bo „host działa" i „host działa użytecznie" to dwie różne
   * rzeczy: pomiar na produkcji pokazał osiemnastokrotną różnicę między
   * hostami, z których oba pobierały skutecznie. Ranking kaskady ma się na tym
   * oprzeć zamiast na założeniach.
   */
  throughputMbps?: number;
  startedAt: string;
  finishedAt: string;
}

export const ACQUIRE_METHODS = ['extract', 'yt-dlp', 'mega', 'gdrive'] as const;
export type AcquireMethod = (typeof ACQUIRE_METHODS)[number];

/** Co odtwarzacz faktycznie dostaje — zawsze nasz własny plik. */
export interface PlayableFile {
  mediaFileId: string;
  /** /api/v1/media/:id/stream — z obsługą Range, ETag i wariantem podpisanym HMAC. */
  streamUrl: string;
  mimeType: string;
  /** true gdy wyjściem jest HLS (długie lub duże materiały) zamiast progresywnego MP4. */
  hls: boolean;
  durationSec: number;
  width: number | null;
  height: number | null;
  posterUrl: string | null;
  spriteUrl: string | null;
  spriteVttUrl: string | null;
}

export const SUBTITLE_FORMATS = ['vtt', 'srt', 'ass', 'ssa'] as const;
export type SubtitleFormat = (typeof SUBTITLE_FORMATS)[number];

/**
 * Skąd wzięła się ścieżka napisów.
 *
 * **Wartości muszą się zgadzać z enumem w `packages/db/src/schema/subtitles.ts`.**
 * Do 13.08.2026 nie zgadzały się: tu stało `'source'`, a baza zna `'sidecar'` —
 * czyli wspólny typ opisywał wartość, której nie da się zapisać, i milczał o tej,
 * która realnie przychodzi z API. Reguła domyślnego włączania napisów przeszła tylko
 * dlatego, że jest listą **dozwolonych** pochodzeń; przy liście zabronionych trafiłaby
 * w martwy warunek i włączała nakładkę nad wtopionymi napisami.
 *
 * `sidecar` to plik dostarczony obok materiału przez to samo źródło (paczka MEGA
 * fansuba) — i właśnie ten przypadek nie włącza się sam.
 */
export const SUBTITLE_ORIGINS = ['embedded', 'sidecar', 'animesub', 'upload'] as const;
export type SubtitleOrigin = (typeof SUBTITLE_ORIGINS)[number];

export interface SubtitleTrack {
  id: string;
  url: string;
  format: SubtitleFormat;
  lang: string;
  label: string;
  origin: SubtitleOrigin;
  default: boolean;
  /** Obecne gdy ścieżka przeszła wyrównanie czasowe (RANSAC): t' = a*t + b. */
  align?: { a: number; b: number; inlierRatio: number };
}

export const SKIP_KINDS = ['op', 'ed', 'recap', 'preview'] as const;
export type SkipKind = (typeof SKIP_KINDS)[number];

export interface SkipRange {
  kind: SkipKind;
  startSec: number;
  endSec: number;
  source: 'aniskip' | 'chapters' | 'manual';
}

/** Raport zdolności dekodowania klienta — decyduje, czy transkodować. */
export const clientCapabilitiesSchema = z.object({
  codecs: z.record(z.string(), z.boolean()),
  hlsNative: z.boolean().optional(),
  userAgent: z.string().max(512).optional(),
});

export type ClientCapabilities = z.infer<typeof clientCapabilitiesSchema>;
